<template>
  <div class="commission-manager">
    <!-- Header Section -->
    <div class="header-section">
      <div class="header-content">
        <div class="title-section">
          <h1 class="page-title">
            <div class="title-icon-wrapper">
              <v-icon class="title-icon">mdi-account-multiple</v-icon>
            </div>
            Commission Management
          </h1>
          <p class="page-subtitle">Manage and track all commission records with ease</p>
        </div>
        <v-btn 
          color="primary" 
          class="add-btn"
          large
          elevation="2"
          @click="AddCommision"
        >
          <v-icon left>mdi-plus-circle</v-icon>
          <span class="btn-text">Add Commission</span>
        </v-btn>
      </div>
    </div>

    <!-- Enhanced Stats Cards -->
    <div class="stats-grid">
      <div class="stat-card active-card">
        <div class="stat-header">
          <div class="stat-icon active">
            <v-icon>mdi-check-circle</v-icon>
          </div>
          <div class="stat-trend positive">
            <v-icon small>mdi-trending-up</v-icon>
          </div>
        </div>
        <div class="stat-content">
          <h3 class="stat-number">{{ activeCommissions }}</h3>
          <p class="stat-label">Active Commissions</p>
          <div class="stat-progress">
            <div class="progress-bar active-progress" :style="{ width: `${(activeCommissions / totalCommissions) * 100}%` }"></div>
          </div>
        </div>
      </div>
      
      <div class="stat-card inactive-card">
        <div class="stat-header">
          <div class="stat-icon inactive">
            <v-icon>mdi mdi-cancel</v-icon>
          </div>
          <div class="stat-trend neutral">
            <v-icon small>mdi-minus</v-icon>
          </div>
        </div>
        <div class="stat-content">
          <h3 class="stat-number">{{ inactiveCommissions }}</h3>
          <p class="stat-label">Inactive Commissions</p>
          <div class="stat-progress">
            <div class="progress-bar inactive-progress" :style="{ width: `${(inactiveCommissions / totalCommissions) * 100}%` }"></div>
          </div>
        </div>
      </div>
      
      <div class="stat-card total-card">
        <div class="stat-header">
          <div class="stat-icon total">
            <v-icon>mdi-chart-timeline-variant</v-icon>
          </div>
          <div class="stat-trend positive">
            <v-icon small>mdi-trending-up</v-icon>
          </div>
        </div>
        <div class="stat-content">
          <h3 class="stat-number">{{ totalCommissions }}</h3>
          <p class="stat-label">Total Commissions</p>
          <div class="stat-progress">
            <div class="progress-bar total-progress" style="width: 100%"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Enhanced Filters and Search -->
    <v-card class="filter-card" elevation="0">
      <v-card-text class="filter-content">
        <div class="filter-row">
          <div class="search-container">
            <v-text-field
              v-model="search"
              prepend-inner-icon="mdi-magnify"
              label="Search commissions..."
              outlined
              dense
              class="search-field"
              clearable
              hide-details
            />
          </div>
          <div class="filter-container">
            <v-select
              v-model="statusFilter"
              :items="statusOptions"
              label="Filter by Status"
              outlined
              dense
              class="status-filter"
              clearable
              hide-details
              prepend-inner-icon="mdi-filter-variant"
            />
          </div>
          <div class="action-buttons-filter">
            <v-btn icon class="filter-btn" @click="refreshData">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
            <!-- <v-btn icon class="filter-btn" @click="exportData">
              <v-icon>mdi-download</v-icon>
            </v-btn> -->
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Enhanced Data Table -->
    <v-card class="table-card" elevation="0">
      <div class="table-header">
        <h3 class="table-title">Commission Records</h3>
        <div class="table-actions">
          <v-chip small outlined color="primary">
            {{ filteredCommissions.length }} Records
          </v-chip>
        </div>
      </div>
      
      <v-data-table
        :headers="headers"
        :items="filteredCommissions"
        :search="search"
        :items-per-page="itemsPerPage"
        :loading="loading"
        class="modern-table"
        mobile-breakpoint="0"
        :footer-props="{
          'items-per-page-options': [5, 10, 15, 20],
          'items-per-page-text': 'Rows per page:'
        }"
      >
        <template v-slot:item._id="{ index }">
          <div class="id-cell">
            <div class="id-badge">{{ String(index + 1).padStart(2, '0') }}</div>
          </div>
        </template>

        <template v-slot:item.commissionName="{ item }">
          <div class="name-cell">
            <div class="name-content">
              <span class="name-text">{{ item.commissionName }}</span>
              <span class="name-subtitle">Commission Plan</span>
            </div>
          </div>
        </template>

        <template v-slot:item.commission_percentage="{ item }">
          <div class="percentage-cell">
            <div class="percentage-container">
              <div class="percentage-progress">
                <v-progress-linear
                  :value="Math.min(item.commission_percentage, 100)"
                  :color="getPercentageColor(item.commission_percentage)"
                  height="8"
                  rounded
                  background-color="rgba(0,0,0,0.05)"
                />
              </div>
              <span class="percentage-text">{{ item.commission_percentage }}%</span>
            </div>
          </div>
        </template>

        <template v-slot:item.status="{ item }">
          <v-chip
            :color="getStatusColor(item.status)"
            :outlined="item.status !== 'Active'"
            small
            class="status-chip"
          >
            <v-icon left small>{{ getStatusIcon(item.status) }}</v-icon>
            <span class="status-text">{{ item.status }}</span>
          </v-chip>
        </template>

        <template v-slot:item.actions="{ item }">
          <div class="action-buttons">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn 
                  icon 
                  color="primary" 
                  v-on="on" 
                  v-bind="attrs" 
                  @click="editCommission(item)" 
                  small
                  class="action-btn"
                >
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
              </template>
              <span>Edit Commission</span>
            </v-tooltip>
            
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn 
                  icon 
                  color="error" 
                  v-on="on" 
                  v-bind="attrs" 
                  @click="deleteCommission(item)" 
                  small
                  class="action-btn"
                >
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </template>
              <span>Delete Commission</span>
            </v-tooltip>
          </div>
        </template>

        <template v-slot:no-data>
          <div class="no-data">
            <div class="no-data-icon">
              <v-icon size="64" color="grey lighten-2">mdi-database-search</v-icon>
            </div>
            <h3 class="no-data-title">No commissions found</h3>
            <p class="no-data-text">Try adjusting your search or filter criteria, or add your first commission</p>
            <v-btn color="primary" outlined  class="no-data-btn" @click="AddCommision">
              <v-icon left>mdi-plus</v-icon>
              Add First Commission
            </v-btn>
          </div>
        </template>

        <template v-slot:loading>
          <div class="loading-container">
            <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
            <p class="loading-text">Loading commissions...</p>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Add Commission Dialog (placeholder) -->
    <add-commission-dialog
     :dialog="showAddDialog"
        :onClose="onCloseDialog"
      />

    <!-- Edit Commission Dialog (placeholder) -->
    <edit-commission-dialog
      :dialog="showEditDialog"
      :onClose="onCloseDialog"
      :editItem="editItem"
      />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import AddCommissionDialog from './AddCommissionDialog.vue'
import EditCommissionDialog from './EditCommissionDialog.vue'
export default {
    components: {
      AddCommissionDialog,
      EditCommissionDialog
    },
  data() {
    return {
      search: '',
      statusFilter: '',
      loading: false,
      showAddDialog: false,
      showEditDialog: false,
      valid: false,
      itemsPerPage: 10,
      editItem: null,
      newCommission: {
        commissionName: '',
        commission_percentage: '',
        status: 'Active'
      },
      headers: [
        { text: 'ID', value: '_id', width: '80px' },
        { text: 'Commission Name', value: 'commissionName', width: '280px' },
        { text: 'Percentage', value: 'commission_percentage', width: '180px' },
        { text: 'Status', value: 'status', width: '130px' },
        { text: 'Actions', value: 'actions', sortable: false, width: '150px', align: 'center' },
      ],
      statusOptions: ['Active', 'Inactive'],
    }
  },
  computed: {
    ...mapGetters({
      allCommissions: 'commission/getAllCommissions',
    }),
    filteredCommissions() {
      let filtered = this.allCommissions || [];
      if (this.statusFilter) {
        filtered = filtered.filter(item => item.status === this.statusFilter);
      }
      return filtered;
    },
    activeCommissions() {
      return this.filteredCommissions.filter(c => c.status === 'Active').length;
    },
    inactiveCommissions() {
      return this.filteredCommissions.filter(c => c.status === 'Inactive').length;
    },
    totalCommissions() {
      return this.filteredCommissions.length;
    }
  },
  methods: {
    ...mapActions({
      commissionList: 'commission/commissionList',
      deleteCommissionAction: 'commission/deleteCommission',
    }),
    getStatusColor(status) {
      return status === 'Active' ? 'success' : 'warning';
    },
    getStatusIcon(status) {
      return status === 'Active' ? 'mdi-check-circle' : 'mdi mdi-cancel';
    },
    getPercentageColor(percentage) {
      if (percentage >= 30) return 'success';
      if (percentage >= 15) return 'warning';
      return 'error';
    },
    deleteCommission(item) {
      const payload ={
        commissionId: item._id
      }
      this.deleteCommissionAction(payload)
        .then(() => {
          this.$toast.success('Commission deleted successfully');
          this.loadCommissions();
        })
        .catch(error => {
          console.error('Error deleting commission:', error);
          this.$toast.error('Failed to delete commission');
        });
    },
    resetForm() {
      this.newCommission = {
        commissionName: '',
        commission_percentage: '',
        status: 'Active'
      };
    },
    AddCommision() {
      this.showAddDialog = true;
    },
    editCommission(item) {
      this.editItem = item;
      this.showEditDialog = true;
    },
    onCloseDialog() {
      this.showAddDialog = false;
      this.showEditDialog = false;
    },
    loadCommissions() {
      this.loading = true;
      this.commissionList()
        .catch(error => {
          console.error('Error loading commissions:', error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    refreshData() {
      this.loadCommissions();
    },
    exportData() {
      console.log('Exporting data...');
    }
  },
  mounted() {
    this.loadCommissions();
  }
}
</script>

<style scoped>
.commission-manager {
  padding: 16px;
  /* background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); */
  min-height: 100vh;
  position: relative;
}

.commission-manager::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
background: 
  radial-gradient(circle at 80% 20%, rgba(8, 68, 104, 0.3)   0%, transparent 50%),
  radial-gradient(circle at 20% 80%, rgba(59, 124, 161, 0.3) 0%, transparent 50%);

  pointer-events: none;
}

.commission-manager > * {
  position: relative;
  z-index: 1;
}

/* Header Section */
.header-section {
  margin-bottom: 32px;
}

.header-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.title-section {
  order: 1;
}

.page-title {
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 1.75rem;
  font-weight: 700;
  color: white;
  margin: 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.title-icon-wrapper {
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.title-icon {
  font-size: 2rem !important;
  color: white;
}

.page-subtitle {
  color: rgba(255, 255, 255, 0.8);
  margin: 8px 0 0 0;
  font-size: 1.1rem;
  font-weight: 400;
}

.add-btn {
  border-radius: 12px;
  text-transform: none;
  font-weight: 600;
  background: linear-gradient(135deg, #4CAF50, #45a049) !important;
  color: white !important;
  order: 2;
  width: 100%;
  height: 48px;
  box-shadow: 0 4px 20px rgba(76, 175, 80, 0.3);
  transition: all 0.3s ease;
}

.add-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 25px rgba(76, 175, 80, 0.4);
}

.btn-text {
  display: inline-block;
  font-size: 1rem;
}

/* Enhanced Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
}

.stat-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 24px;
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.1),
    0 2px 8px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.stat-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #4CAF50, #45a049);
}

.stat-card.inactive-card::before {
  background: linear-gradient(90deg, #FF9800, #F57C00);
}

.stat-card.total-card::before {
  background: linear-gradient(90deg, #2196F3, #1976D2);
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 
    0 12px 40px rgba(0, 0, 0, 0.15),
    0 4px 12px rgba(0, 0, 0, 0.1);
}

.stat-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
}

.stat-icon.active {
  background: linear-gradient(135deg, #4CAF50, #66BB6A);
}

.stat-icon.inactive {
  background: linear-gradient(135deg, #FF9800, #FFB74D);
}

.stat-icon.total {
  background: linear-gradient(135deg, #2196F3, #64B5F6);
}

.stat-trend {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-trend.positive {
  background: rgba(76, 175, 80, 0.1);
  color: #4CAF50;
}

.stat-trend.neutral {
  background: rgba(158, 158, 158, 0.1);
  color: #9E9E9E;
}

.stat-number {
  font-size: 2rem;
  font-weight: 700;
  color: #1a237e;
  margin: 0 0 4px 0;
  line-height: 1;
}

.stat-label {
  color: #666;
  margin: 0 0 12px 0;
  font-weight: 500;
  font-size: 0.95rem;
}

.stat-progress {
  height: 4px;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 2px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  border-radius: 2px;
  transition: width 0.5s ease;
}

.active-progress {
  background: linear-gradient(90deg, #4CAF50, #66BB6A);
}

.inactive-progress {
  background: linear-gradient(90deg, #FF9800, #FFB74D);
}

.total-progress {
  background: linear-gradient(90deg, #2196F3, #64B5F6);
}

/* Enhanced Filter Section */
.filter-card {
  margin-bottom: 20px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.filter-content {
  padding: 20px;
}

.filter-row {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.search-container, .filter-container {
  flex: 1;
}

.action-buttons-filter {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.filter-btn {
  background: rgba(103, 126, 234, 0.1) !important;
  color: #677eea !important;
}

/* Enhanced Table Styles */
.table-card {
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  overflow: hidden;
}

.table-header {
  padding: 20px 24px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(103, 126, 234, 0.02);
}

.table-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a237e;
  margin: 0;
}

.modern-table {
  background: transparent;
}

/* Enhanced Table Cell Styles */
.id-cell {
  padding: 8px 0;
}

.id-badge {
  background: linear-gradient(135deg, #E3F2FD, #BBDEFB);
  color: #1976D2;
  padding: 6px 12px;
  border-radius: 16px;
  font-weight: 600;
  font-size: 0.85rem;
  display: inline-block;
  border: 1px solid rgba(25, 118, 210, 0.2);
}

.name-cell {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
}

.name-avatar {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.name-content {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.name-text {
  font-weight: 600;
  color: #1a237e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 0.95rem;
}

.name-subtitle {
  font-size: 0.75rem;
  color: #666;
  font-weight: 500;
}

.percentage-cell {
  padding: 8px 0;
}

.percentage-container {
  display: flex;
  align-items: center;
  gap: 12px;
}

.percentage-progress {
  flex: 1;
  min-width: 60px;
}

.percentage-text {
  font-weight: 600;
  color: #1a237e;
  min-width: 40px;
  font-size: 0.9rem;
}

.status-chip {
  font-weight: 600;
  font-size: 0.8rem;
  border-radius: 12px;
}

.status-text {
  display: inline-block;
}

.action-buttons {
  display: flex;
  gap: 6px;
  justify-content: center;
}

.action-btn {
  background: rgba(0, 0, 0, 0.04) !important;
  transition: all 0.2s ease;
}

.action-btn:hover {
  transform: scale(1.1);
}

/* Enhanced No Data and Loading States */
.no-data {
  text-align: center;
  padding: 48px 24px;
  color: #666;
}

.no-data-icon {
  margin-bottom: 16px;
}

.no-data-title {
  margin: 0 0 8px 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a237e;
}

.no-data-text {
  font-size: 0.95rem;
  margin-bottom: 24px;
  color: #666;
}

.no-data-btn {
  border-radius: 12px;
  text-transform: none;
  font-weight: 600;
}

.loading-container {
  text-align: center;
  padding: 48px 24px;
}

.loading-text {
  margin-top: 16px;
  color: #666;
  font-weight: 500;
}

/* Dialog Styles */
.dialog-card {
  border-radius: 16px;
}

.dialog-title {
  background: linear-gradient(135deg, #f5f7fa, #c3cfe2);
  color: #1a237e;
  font-weight: 600;
}

.dialog-placeholder {
  text-align: center;
  color: #666;
  padding: 24px;
}

.dialog-actions {
  padding: 16px 24px;
}

/* Responsive Design */
@media (min-width: 600px) {
  .commission-manager {
    padding: 24px;
  }

  .header-content {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
  }

  .add-btn {
    width: auto;
    order: 2;
    min-width: 180px;
  }

  .stats-grid {
    grid-template-columns: repeat(3, 1fr);
  }

  .filter-row {
    flex-direction: row;
    align-items: center;
  }

  .search-container {
    flex: 2;
  }

  .filter-container {
    flex: 1;
  }

  .action-buttons-filter {
    flex: 0 0 auto;
  }

  .page-title {
    font-size: 2rem;
  }
}

@media (min-width: 960px) {
  .commission-manager {
    padding: 32px;
    max-width: 1400px;
    margin: 0 auto;
  }

  .stats-grid {
    gap: 24px;
  }

  .stat-card {
    padding: 28px;
  }

  .stat-number {
    font-size: 2.25rem;
  }

  .page-title {
    font-size: 2.25rem;
  }
}

/* Mobile Specific Adjustments */
@media (max-width: 599px) {
  .name-cell {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  .percentage-container {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  .action-buttons {
    flex-wrap: wrap;
    gap: 4px;
  }

  .stats-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .stat-card {
    padding: 20px;
  }
}

/* Dark theme support */
/* @media (prefers-color-scheme: dark) {
  .commission-manager {
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  }
  
  .stat-card, .filter-card, .table-card {
    background: rgba(255, 255, 255, 0.05);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.1);
  }
  
  .page-title, .stat-number, .name-text, .table-title {
    color: white;
  }
  
  .page-subtitle, .stat-label, .name-subtitle {
    color: rgba(255, 255, 255, 0.7);
  }
} */
</style>